import React, { Dispatch, SetStateAction } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { GetCallResponse } from '@distribute/shared/api-types/call';
import {
  Icon,
  Avatar,
  TimeAgo,
  RecordState,
  Button,
} from '../../../../../shared/ui';
import { IconMap } from '../../../../../shared/sprite';
import {
  checkIsMuxLoading,
  checkIsMuxReady,
  getMuxAssetImageByPolicy,
} from '@distribute/shared/utils';
import { formatMsToTimePassed } from '@distribute/shared/utils';
import { callModel } from '../../../../../features/call';
import { InsertVariant } from './lib/config';
import { InsertCallButton } from './InsertCallButton';

type CallItemProps = {
  item: GetCallResponse;
  setMakePublicConfirmationCallId: Dispatch<SetStateAction<string | null>>;
  setInsertVariant: Dispatch<SetStateAction<InsertVariant | null>>;
};

export const CallItem: React.FC<CallItemProps> = ({
  item,
  setMakePublicConfirmationCallId,
  setInsertVariant,
}) => {
  const dispatch = useDispatch();

  const callListOptions = useSelector(
    callModel.selectors.selectCallListOptions
  );

  const { isPrivate, userToTeam, name, createdAt, muxAsset } = item;

  const owner = userToTeam.user;

  const handlePreviewClick = () => {
    dispatch(callModel.actions.loadCallDetailsForEditor({ id: item.id }));
  };

  return (
    <div className="p-3 border border-gray-300 rounded-lg cursor-pointer group bg-base-white hover:shadow-sm">
      <div className="h-41.5 overflow-hidden rounded-lg relative border border-base-black/[0.08] mb-4">
        {checkIsMuxReady(muxAsset) ? (
          <>
            <img
              className="block object-cover w-full h-full group-hover:blur"
              src={getMuxAssetImageByPolicy(muxAsset)}
              alt={item.name}
            />
            <span className="absolute bottom-2 right-2 bg-base-black/70 text-base-white py-0.5 px-2.25 rounded-2xl text-sm group-hover:blur">
              {formatMsToTimePassed(muxAsset.data.duration * 1000)}
            </span>
          </>
        ) : (
          <RecordState loading={checkIsMuxLoading(muxAsset)} />
        )}
        <div className="absolute top-0 left-0 z-10 flex flex-col items-center justify-center w-full h-full gap-2 transition-all duration-100 ease-linear opacity-0 group-hover:opacity-100">
          <InsertCallButton
            call={item}
            setInsertVariant={setInsertVariant}
            setMakePublicConfirmationCallId={setMakePublicConfirmationCallId}
          />

          <Button
            variant="text"
            color="secondary"
            className="w-45 !h-9 text-sm"
            onClick={handlePreviewClick}
          >
            <Icon glyph={IconMap.Eye} width={20} className="mr-1.5" />
            Preview
          </Button>
        </div>
      </div>

      <div className="flex flex-col gap-1.5 text-gray-700">
        {owner &&
          (callListOptions.type === 'team' || callListOptions.search) && (
            <div className="flex items-center gap-1 text-xs">
              <Avatar
                src={owner.photoUrl}
                displayName={owner.displayName}
                size="3xs"
              />
              by{' '}
              <span className="font-semibold text-gray-900">
                {owner.displayName}
              </span>
            </div>
          )}
        <h3 className="font-semibold truncate">{name}</h3>
        <div className="flex items-center justify-between text-sm text-gray-600">
          <TimeAgo datetime={createdAt} locale="my" />
          {isPrivate && (
            <p className="flex items-center gap-0.5">
              <Icon glyph={IconMap.Lock} width={16} />
              Private
            </p>
          )}
          {!isPrivate && (
            <p className="flex items-center gap-0.5">
              <Icon glyph={IconMap.Users01} width={16} />
              Team access
            </p>
          )}
        </div>
      </div>
    </div>
  );
};
